import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { ApiService, Empregador, JornadaHorario, JornadaPonto } from '../../api.service';

export type CarregamentoAbaRelogio = 'relogio' | 'jornadas' | 'terminais';

@Injectable({ providedIn: 'root' })
export class ServicoRelogioPonto {
  constructor(private readonly api: ApiService) {}

  carregamentoAba(aba: 'horarios' | 'jornadas' | 'terminais'): CarregamentoAbaRelogio {
    if (aba === 'horarios') {
      return 'relogio';
    }
    if (aba === 'jornadas') {
      return 'jornadas';
    }
    return 'terminais';
  }

  prepararConfiguracao(empregador?: Empregador): Empregador {
    if (!empregador) {
      return this.configuracaoPadrao();
    }

    return {
      ...empregador,
      horarioPrimeiraBatida: empregador.horarioPrimeiraBatida || '08:00',
      horarioSegundaBatida: empregador.horarioSegundaBatida || '11:00',
      horarioTerceiraBatida: empregador.horarioTerceiraBatida || '13:00',
      horarioQuartaBatida: empregador.horarioQuartaBatida || '17:00',
      toleranciaMinutos: empregador.toleranciaMinutos ?? 15,
      validarJanelaPonto: empregador.validarJanelaPonto ?? true
    };
  }

  restaurarPadrao(relogio: Empregador): Empregador {
    return {
      ...relogio,
      horarioPrimeiraBatida: '08:00',
      horarioSegundaBatida: '11:00',
      horarioTerceiraBatida: '13:00',
      horarioQuartaBatida: '17:00',
      toleranciaMinutos: 15,
      validarJanelaPonto: true
    };
  }

  salvar(relogio: Empregador): Observable<Empregador> {
    return this.api.salvarEmpregador(relogio);
  }

  novaJornada(empregadorId = ''): JornadaPonto {
    return {
      empregadorId,
      nome: 'Jornada padrao',
      padrao: false,
      ativa: true,
      validarJanelaPonto: true,
      toleranciaMinutos: 15,
      horarios: [
        this.novoHorario(1, 'ENTRADA', '08:00', 15),
        this.novoHorario(2, 'SAIDA', '11:00', 15),
        this.novoHorario(3, 'ENTRADA', '13:00', 15),
        this.novoHorario(4, 'SAIDA', '17:00', 15)
      ]
    };
  }

  prepararJornada(jornada: JornadaPonto): JornadaPonto {
    return {
      ...jornada,
      toleranciaMinutos: jornada.toleranciaMinutos ?? 15,
      validarJanelaPonto: jornada.validarJanelaPonto ?? true,
      horarios: [...(jornada.horarios || [])]
        .sort((a, b) => a.sequencia - b.sequencia)
        .map((horario, indice) => ({
          ...horario,
          sequencia: horario.sequencia || indice + 1,
          tipo: horario.tipo || ((indice + 1) % 2 === 1 ? 'ENTRADA' : 'SAIDA'),
          toleranciaAntesMinutos: horario.toleranciaAntesMinutos ?? jornada.toleranciaMinutos ?? 15,
          toleranciaDepoisMinutos: horario.toleranciaDepoisMinutos ?? jornada.toleranciaMinutos ?? 15,
          diasSemana: horario.diasSemana || '1,2,3,4,5,6,7',
          ativo: horario.ativo ?? true
      }))
    };
  }

  selecionarJornadaCadastro(jornadas: JornadaPonto[], empregadorId = ''): JornadaPonto {
    return jornadas.find(jornada => jornada.padrao)
      || jornadas[0]
      || this.novaJornada(empregadorId);
  }

  jornadaQueValeNoPonto(jornadas: JornadaPonto[]) {
    const ativas = (jornadas || []).filter(jornada => jornada.ativa);
    return ativas.find(jornada => jornada.padrao)
      || [...ativas].sort((a, b) => (b.versao || 0) - (a.versao || 0))[0];
  }

  selecionarJornada(jornadas: JornadaPonto[], jornadaId: string, empregadorId = ''): JornadaPonto {
    const encontrada = jornadas.find(item => item.id === jornadaId);
    return encontrada ? this.prepararJornada(encontrada) : this.novaJornada(empregadorId);
  }

  adicionarHorario(jornada: JornadaPonto): JornadaPonto {
    const sequencia = (jornada.horarios?.length || 0) + 1;
    return {
      ...jornada,
      horarios: [
        ...(jornada.horarios || []),
        this.novoHorario(sequencia, sequencia % 2 === 1 ? 'ENTRADA' : 'SAIDA', '08:00', jornada.toleranciaMinutos || 0)
      ]
    };
  }

  removerHorario(jornada: JornadaPonto, indice: number): JornadaPonto {
    return {
      ...jornada,
      horarios: jornada.horarios
        .filter((_, atual) => atual !== indice)
        .map((horario, atual) => ({ ...horario, sequencia: atual + 1 }))
    };
  }

  listarJornadas(empregadorId: string): Observable<JornadaPonto[]> {
    return this.api.listarJornadas(empregadorId);
  }

  listarJornadasPorEmpregadorSelecionado(empregadorId: string): Observable<JornadaPonto[]> {
    if (!empregadorId) {
      return of([]);
    }

    return this.listarJornadas(empregadorId);
  }

  salvarJornada(jornada: JornadaPonto): Observable<JornadaPonto> {
    return this.api.salvarJornada(this.prepararJornada(jornada));
  }

  excluirJornada(jornadaId: string): Observable<void> {
    return this.api.excluirJornada(jornadaId);
  }

  mensagemEmpregadorObrigatorioJornada() {
    return 'Selecione um empregador para cadastrar a jornada.';
  }

  mensagemJornadaSalva(_jornada: JornadaPonto, editando = false) {
    return editando
      ? 'Sucesso.\nRegistro alterado com sucesso.'
      : 'Sucesso.\nRegistro salvo com sucesso.';
  }

  mensagemErroSalvarJornada() {
    return 'Erro ao salvar jornada.';
  }

  mensagemConfirmarExclusaoJornada(jornada: JornadaPonto) {
    return `Excluir jornada ${jornada.nome}?`;
  }

  mensagemJornadaExcluida() {
    return 'Sucesso.\nRegistro excluído com sucesso.';
  }

  mensagemErroExcluirJornada() {
    return 'Erro ao excluir jornada.';
  }

  private novoHorario(sequencia: number, tipo: string, horario: string, tolerancia: number): JornadaHorario {
    return {
      sequencia,
      tipo,
      horario,
      toleranciaAntesMinutos: tolerancia,
      toleranciaDepoisMinutos: tolerancia,
      diasSemana: '1,2,3,4,5,6,7',
      ativo: true
    };
  }

  private configuracaoPadrao(): Empregador {
    return {
      razaoSocial: '',
      nomeFantasia: '',
      documento: '',
      horarioPrimeiraBatida: '08:00',
      horarioSegundaBatida: '11:00',
      horarioTerceiraBatida: '13:00',
      horarioQuartaBatida: '17:00',
      toleranciaMinutos: 15,
      validarJanelaPonto: true,
      ativo: true
    };
  }
}
